/**
 * Variant option priority (prototype rule, `variant-option-priority.md`).
 *
 * Options are in priority order. Shopify already marks a value unavailable only from the options
 * before it, so earlier options are never disabled by later ones. This adds the selection rule:
 * when an option changes and the combination with the later selections isn't available, the
 * later selections move to the first available variant that matches the changed and earlier
 * options, keeping as many later selections as possible (in option order).
 *
 * It runs in the capture phase, before Horizon's variant picker handles the change, and only
 * updates which later values are checked; Horizon then fetches and renders that variant.
 */

/** @type {Map<string, { available: boolean, options: string[] }[]>} Keyed by product id (quick view reuses its picker). */
const variantCache = new Map();

/** @param {HTMLElement} picker */
function variantsFor(picker) {
  const productId = picker.dataset.productId ?? '';
  const cached = variantCache.get(productId);
  if (cached) return cached;
  const script = document.querySelector(`script[data-hl-variants="${productId}"]`);
  if (!script?.textContent) return null;
  try {
    const variants = JSON.parse(script.textContent);
    variantCache.set(productId, variants);
    return variants;
  } catch {
    return null;
  }
}

/**
 * Current value per option, in option order.
 * @param {HTMLElement} picker
 * @returns {{ fieldset: Element, value: string | undefined }[]}
 */
function readSelection(picker) {
  // Every option (button fieldset or dropdown wrapper) is a `.variant-option`, in option order.
  return [...picker.querySelectorAll('.variant-option')]
    .map((fieldset) => {
      // Buttons (radios) are the picker's source of truth; a select only for dropdown-style options.
      if (fieldset.querySelector('input[type="radio"]')) {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return { fieldset, value: checked instanceof HTMLInputElement ? checked.value : undefined };
      }
      const select = fieldset.querySelector('select');
      return { fieldset, value: select ? select.value : undefined };
    });
}

/** @param {Event} event */
function handleChange(event) {
  const picker = /** @type {HTMLElement} */ (event.currentTarget);
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

  const variants = variantsFor(picker);
  if (!variants?.length) return;

  const selection = readSelection(picker);
  // Changes from the quick view's mirror select are applied by clicking the matching button.
  if (target.classList.contains('hl-qv-select')) return;
  const changedIndex = selection.findIndex(({ fieldset }) => fieldset.contains(target));
  if (changedIndex < 0 || changedIndex === selection.length - 1) return;

  const values = selection.map(({ value }) => value);
  const candidates = variants.filter(
    (variant) =>
      variant.available &&
      variant.options[changedIndex] === values[changedIndex] &&
      values.slice(0, changedIndex).every((value, index) => value === undefined || variant.options[index] === value)
  );
  const first = candidates[0];
  if (!first) return;

  // Prefer the candidate that keeps the later selections, compared in option order.
  const preferred = candidates.reduce((best, candidate) => {
    for (let index = changedIndex + 1; index < values.length; index += 1) {
      const candidateKeeps = candidate.options[index] === values[index];
      const bestKeeps = best.options[index] === values[index];
      if (candidateKeeps !== bestKeeps) return candidateKeeps ? candidate : best;
    }
    return best;
  }, first);

  const pickerApi = /** @type {HTMLElement & { updateSelectedOption?: (target: Element) => void }} */ (picker);
  for (let index = changedIndex + 1; index < selection.length; index += 1) {
    const wanted = preferred.options[index];
    const entry = selection[index];
    if (!entry || wanted === undefined || wanted === values[index]) continue;

    const input = [...entry.fieldset.querySelectorAll('input[type="radio"]')].find(
      (element) => element instanceof HTMLInputElement && element.value === wanted
    );
    if (input instanceof HTMLInputElement) {
      input.checked = true;
      pickerApi.updateSelectedOption?.(input);
      continue;
    }
    const select = entry.fieldset.querySelector('select');
    if (select) {
      select.value = wanted;
      pickerApi.updateSelectedOption?.(select);
    }
  }
}

/** @param {ParentNode} root */
function enhance(root) {
  for (const picker of root.querySelectorAll('variant-picker')) {
    if (!(picker instanceof HTMLElement) || picker.dataset.hlPriority) continue;
    picker.dataset.hlPriority = 'true';
    picker.addEventListener('change', handleChange, { capture: true });
  }
}

enhance(document);
// Pickers are re-rendered by morphing, which keeps the element; new pickers (quick view) are caught here.
new MutationObserver(() => enhance(document)).observe(document.body, { childList: true, subtree: true });
